/** 水平滚动到中心位置的配置选项 */
export type ScrollToCenterXOptions = {
  /** 滚动行为，默认为 'smooth' 平滑滚动 */
  behavior?: ScrollBehavior;
  /** 偏移量，正数向右偏移，负数向左偏移 */
  offset?: number;
  /** 是否限制在有效滚动范围内，防止超出边界 */
  clamp?: boolean;
};

/** 默认配置 */
const defaultOptions: Required<ScrollToCenterXOptions> = {
  behavior: "smooth",
  offset: 0,
  clamp: true,
};

/**
 * 计算目标元素居中所需的滚动距离
 *
 * @param container 容器元素
 * @param target 目标子元素
 * @param offset 偏移量
 * @returns 计算后的 scrollLeft 值
 */
export function computeCenterScrollLeft(
  container: HTMLElement,
  target: HTMLElement,
  offset: number = 0,
): number {
  const containerRect = container.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();

  const containerWidth = containerRect.width;
  const currentScrollLeft = container.scrollLeft;
  const targetLeft = targetRect.left - containerRect.left + currentScrollLeft;
  const targetWidth = targetRect.width;

  // 计算目标元素居中所需的滚动位置
  const desiredLeft = targetLeft - (containerWidth - targetWidth) / 2 + offset;
  const maxScrollLeft = container.scrollWidth - containerWidth;

  // 限制在有效滚动范围内
  return Math.max(0, Math.min(desiredLeft, Math.max(0, maxScrollLeft)));
}

/**
 * 滚动子元素到容器水平中心位置
 *
 * @param container 容器元素
 * @param target 目标子元素
 * @param options 滚动配置选项
 */
export function scrollChildIntoCenterX(
  container: HTMLElement,
  target: HTMLElement,
  options?: ScrollToCenterXOptions,
) {
  const { behavior, offset, clamp } = { ...defaultOptions, ...options };
  const left = clamp
    ? computeCenterScrollLeft(container, target, offset)
    : target.offsetLeft -
      (container.clientWidth - target.clientWidth) / 2 +
      offset;
  container.scrollTo({ left, behavior });
}

/**
 * 通过选择器查找子元素并滚动到容器水平中心位置
 *
 * @param container 容器元素
 * @param selector CSS 选择器
 * @param options 滚动配置选项
 */
export function scrollChildSelectorIntoCenterX(
  container: HTMLElement,
  selector: string,
  options?: ScrollToCenterXOptions,
) {
  const target = container.querySelector(selector) as HTMLElement | null;
  if (!container || !target) return;
  scrollChildIntoCenterX(container, target, options);
}
